import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';

/**
 * Everything that owns a glyph: the four Envolve Hub applications and the
 * Administration area (`admin`, the gear). Reused by the sidebar menu model
 * and the masthead so every consumer keys off the same union. The apps alone
 * are `AppKey` in `core/layout/app-registry.ts`.
 */
export type AppGlyphKey = 'om' | 'gemba' | 'wm' | 'dash' | 'admin';

/** Pixel sizes the glyph is drawn at: 16 in the sidebar, 24 in the masthead tile. */
export type AppGlyphSize = 16 | 24;

/**
 * One primitive of a glyph. The shapes are ported verbatim from
 * `docs/design/app-headers/boards/AppGlyph.dc.html` (24×24 grid), so they are
 * kept as structured data rather than flattened into a single path.
 */
export type AppGlyphShape =
  | { readonly kind: 'path'; readonly d: string }
  | { readonly kind: 'rect'; readonly x: number; readonly y: number; readonly width: number; readonly height: number; readonly rx: number }
  | { readonly kind: 'ellipse'; readonly cx: number; readonly cy: number; readonly rx: number; readonly ry: number }
  | { readonly kind: 'circle'; readonly cx: number; readonly cy: number; readonly r: number };

/**
 * The shared glyph set — one monoline glyph per app, 1.75px stroke on a 24px
 * grid, inheriting `currentColor` (see docs/design/app-headers/README.md,
 * "Shared glyph set"; the gear is from docs/design/admin-headers/README.md,
 * "Glyph candidates").
 *
 * | key     | area                   | glyph        |
 * | ------- | ---------------------- | ------------ |
 * | `om`    | Opportunity Management | bolt         |
 * | `gemba` | Gemba                  | footsteps    |
 * | `wm`    | Workspace Management   | five squares |
 * | `dash`  | Dashboards             | bars         |
 * | `admin` | Administration         | gear         |
 */
export const APP_GLYPHS: Readonly<Record<AppGlyphKey, readonly AppGlyphShape[]>> = {
  om: [{ kind: 'path', d: 'M13 2.5 4.5 13.5H11L10 21.5l8.5-11H12l1-8z' }],
  gemba: [
    { kind: 'ellipse', cx: 7.5, cy: 12, rx: 2.6, ry: 4 },
    { kind: 'circle', cx: 7.5, cy: 19.3, r: 1.6 },
    { kind: 'ellipse', cx: 16.5, cy: 6, rx: 2.6, ry: 4 },
    { kind: 'circle', cx: 16.5, cy: 13.3, r: 1.6 },
  ],
  wm: [
    { kind: 'rect', x: 3, y: 3, width: 5, height: 5, rx: 1.2 },
    { kind: 'rect', x: 16, y: 3, width: 5, height: 5, rx: 1.2 },
    { kind: 'rect', x: 9.5, y: 9.5, width: 5, height: 5, rx: 1.2 },
    { kind: 'rect', x: 3, y: 16, width: 5, height: 5, rx: 1.2 },
    { kind: 'rect', x: 16, y: 16, width: 5, height: 5, rx: 1.2 },
  ],
  dash: [
    { kind: 'path', d: 'M3.5 20.5h17' },
    { kind: 'path', d: 'M7 17v-5' },
    { kind: 'path', d: 'M12 17V6' },
    { kind: 'path', d: 'M17 17V9.5' },
  ],
  // Eight teeth on a 9.75 / 7.25 radius, hub r=3; generated for the
  // admin-headers glyph board (docs/design/admin-headers/boards/AdminGlyph.dc.html).
  admin: [
    {
      kind: 'path',
      d: 'M10.25 4.97L10.47 2.37L13.53 2.37L13.75 4.97L15.73 5.79L17.73 4.11L19.89 6.27L18.21 8.27L19.03 10.25L21.63 10.47L21.63 13.53L19.03 13.75L18.21 15.73L19.89 17.73L17.73 19.89L15.73 18.21L13.75 19.03L13.53 21.63L10.47 21.63L10.25 19.03L8.27 18.21L6.27 19.89L4.11 17.73L5.79 15.73L4.97 13.75L2.37 13.53L2.37 10.47L4.97 10.25L5.79 8.27L4.11 6.27L6.27 4.11L8.27 5.79Z',
    },
    { kind: 'circle', cx: 12, cy: 12, r: 3 },
  ],
};

/** Every glyph key, in sidebar order (the apps, then Administration). Handy for stories and menu models. */
export const APP_GLYPH_KEYS: readonly AppGlyphKey[] = ['om', 'gemba', 'wm', 'dash', 'admin'];

/**
 * **App glyph** — the monoline icon that identifies an Envolve Hub application
 * or the Administration area (sidebar rows, masthead tile). Renders one inline SVG that inherits the
 * surrounding text colour, so the same glyph works on white, on the grey tile
 * and on the indigo topbar without any colour input.
 *
 * Accessibility: decorative by default (`aria-hidden="true"`, no role). Pass
 * `label` when the glyph is the only thing conveying the app name (e.g. an
 * icon-only sidebar) and it becomes `role="img"` with that `aria-label`.
 *
 * Usage:
 * ```html
 * <app-glyph app="om" />
 * <app-glyph app="gemba" [size]="16" label="Gemba" />
 * ```
 */
@Component({
  standalone: true,
  selector: 'app-glyph',
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <svg
      viewBox="0 0 24 24"
      [attr.width]="size()"
      [attr.height]="size()"
      fill="none"
      stroke="currentColor"
      stroke-width="1.75"
      stroke-linecap="round"
      stroke-linejoin="round"
      focusable="false"
      [attr.aria-hidden]="label() ? null : 'true'"
      [attr.role]="label() ? 'img' : null"
      [attr.aria-label]="label() ?? null"
    >
      @for (shape of shapes(); track $index) {
        @switch (shape.kind) {
          @case ('path') {
            <svg:path [attr.d]="shape.d" />
          }
          @case ('rect') {
            <svg:rect
              [attr.x]="shape.x"
              [attr.y]="shape.y"
              [attr.width]="shape.width"
              [attr.height]="shape.height"
              [attr.rx]="shape.rx"
            />
          }
          @case ('ellipse') {
            <svg:ellipse [attr.cx]="shape.cx" [attr.cy]="shape.cy" [attr.rx]="shape.rx" [attr.ry]="shape.ry" />
          }
          @case ('circle') {
            <svg:circle [attr.cx]="shape.cx" [attr.cy]="shape.cy" [attr.r]="shape.r" />
          }
        }
      }
    </svg>
  `,
  styles: [
    `
      :host {
        display: inline-flex;
        flex-shrink: 0;
        line-height: 0;
        vertical-align: middle;
      }
      svg {
        display: block;
      }
    `,
  ],
})
export class AppGlyphComponent {
  /** Which application's glyph to draw. */
  readonly app = input.required<AppGlyphKey>();
  /** Rendered size in px (also the SVG width/height attributes). */
  readonly size = input<AppGlyphSize>(24);
  /** Accessible name. Unset = decorative (`aria-hidden`); set = `role="img"` + `aria-label`. */
  readonly label = input<string>();

  /** The shapes for the selected app, straight from {@link APP_GLYPHS}. */
  readonly shapes = computed(() => APP_GLYPHS[this.app()]);
}
